/* eslint-disable max-classes-per-file */
import React, {
    ReactElement, useContext, useEffect, useState,
} from "react";
import { GraphQLError } from "graphql";
import {
    Permission,
    PortalEvent,
    PortalEventType,
    PortalRegisterEvent,
    useSubscribeToPortalMutation,
} from "../__generated__/api";
import useCurrentSession from "./UserContext";
import useLogging from "./logging/LoggingContext";
import { hasLoginError } from "../api";

export type Handler<T> = (event: T) => void;

const PING_INTERVAL = 1000 * 60 * 2;

export enum ConnectionEvent {
    CONNECTED = "CONNECTED",
    RECONNECTED = "RECONNECTED",
    DISCONNECTED = "DISCONNECTED"
}

class ScopedClientApi {
    private handlers: {event: PortalEventType | ConnectionEvent; handler: Handler<PortalEvent | never>; priority: number}[] = [];

    private api: ClientApi;

    constructor(api: ClientApi) {
        this.api = api;
    }

    public on<T extends PortalEvent>(event: PortalEventType | ConnectionEvent, priority: number, handler: Handler<T>): void {
        this.handlers.push({ event, priority, handler: handler as Handler<PortalEvent> });
        this.api.on(event, priority, handler);
    }

    public allOff() {
        this.handlers.forEach(({ event, handler }) => {
            this.api.off(event, handler);
        });
    }
}

interface HandlerRow {
    priority: number;
    handler: Handler<PortalEvent | never>
}

export class ClientApi {
    private socket?: WebSocket;

    private _isConnecting = false;

    private connectionCount = 0;

    private events: Record<PortalEventType | ConnectionEvent, HandlerRow[]> = Object.create(null);

    public get isConnecting(): boolean {
        return this._isConnecting;
    }

    public set isConnecting(value: boolean) {
        this._isConnecting = value;
    }

    public registerEvents(handler: (api: ScopedClientApi) => void, dependencies?: unknown[]): void {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const client = new ScopedClientApi(this);
            handler(client);
            return () => {
                client.allOff();
            };
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, dependencies ?? []);
    }

    public on<T extends PortalEvent>(event: PortalEventType | ConnectionEvent, priority: number, handler: Handler<T>): void {
        this.events[event] = this.events[event] ?? [];
        const events = this.events[event];
        events.push({ handler: handler as Handler<PortalEvent>, priority });
        this.events[event] = events.sortBy("priority");
    }

    public off<T extends PortalEvent>(event: PortalEventType | ConnectionEvent, handler: Handler<T>): void {
        const list = this.events[event];
        if (!list) {
            return;
        }

        const index = list.findIndex(listHandler => listHandler.handler === handler);
        if (index >= 0) {
            list.splice(index, 1);
        }
    }

    private emit(event: PortalEventType | ConnectionEvent, data?: PortalEvent): void {
        if (!this.events[event]) {
            return;
        }
        this.events[event].forEach(evt => {
            try {
                evt.handler(data as PortalEvent | never);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Error sending event", err);
            }
        });
    }

    public async connect(currentUrl: string, serverUrl: string, sessionToken: string): Promise<void> {
        if (this.socket) {
            return;
        }
        this.socket = new WebSocket(serverUrl);
        this.register(currentUrl, sessionToken);
    }

    public send(event: PortalEvent): void {
        this.socket?.send(JSON.stringify(event));
    }

    public close(): void {
        this.socket?.close();
    }

    private register(currentUrl: string, sessionToken: string) {
        let connectedPing: number | undefined;
        this.socket?.addEventListener("open", () => {
            if (this.connectionCount === 0) {
                this.emit(ConnectionEvent.CONNECTED);
            } else {
                this.emit(ConnectionEvent.RECONNECTED);
            }
            this.connectionCount += 1;
            setTimeout(() => {
                this.send({
                    url: currentUrl,
                    sessionToken: sessionToken as string,
                    action: PortalEventType.Register,
                } as PortalRegisterEvent);
            }, 300);
            connectedPing = setInterval(() => {
                this.send({
                    action: PortalEventType.Ping,
                });
            }, PING_INTERVAL) as unknown as number;
        });
        const close = () => {
            clearInterval(connectedPing);
            try {
                this.socket?.close();
            } catch {
                // dont do anything then.
            }
            delete this.socket;
            this.emit(ConnectionEvent.DISCONNECTED);
        };
        this.socket?.addEventListener("close", close);
        this.socket?.addEventListener("error", close);
        this.socket?.addEventListener("message", event => {
            const data = (
                typeof event.data === "string" && event.data ? JSON.parse(event.data) : event.data
            ) as PortalEvent & {action: PortalEventType};
            this.emit(data.action, data);
        });
    }
}

export const PortalSessionContext = React.createContext<ClientApi>(new ClientApi());

export function PortalSubscriptionProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [subscribeToPortal] = useSubscribeToPortalMutation();
    const session = useCurrentSession();
    const [api] = useState<ClientApi>(() => new ClientApi());
    let canConnect = session.isLoggedIn && (
        session.permissions.has(Permission.ChatsModifyAll)
                || session.permissions.has(Permission.TicketsReadSelf)
                || session.permissions.has(Permission.TicketsReadAll));
    const logging = useLogging();
    useEffect(() => {
        if (!canConnect) {
            return;
        }
        (async () => {
            let lastReconnect = 1000;
            do {
                // eslint-disable-next-line no-loop-func
                const promise = new Promise<void>(resolve => {
                    (async () => {
                        try {
                            const subscription = await subscribeToPortal({
                                fetchPolicy: "no-cache",
                            });
                            if (hasLoginError(subscription.errors)) {
                                session.clearCache();
                            }
                            if (!subscription.data) {
                                return;
                            }
                            const { sessionToken, socketUrl } = subscription.data.subscribe;
                            api.connect(window.location.toString(), socketUrl, sessionToken);
                            api.on(ConnectionEvent.DISCONNECTED, 100, () => {
                                // reconnect after 1 second
                                setTimeout(resolve, lastReconnect);
                                lastReconnect = Math.min(10000, lastReconnect * 2);
                                logging.debug({}, "Disconnected from subscription");
                            });
                            api.on(ConnectionEvent.CONNECTED, 100, () => {
                                lastReconnect = 1000;
                                logging.debug({}, "Connected to subscription.");
                            });
                            api.on(ConnectionEvent.RECONNECTED, 100, () => {
                                lastReconnect = 1000;
                            });
                        } catch (err) {
                            if (hasLoginError([err as GraphQLError])) {
                                session.clearCache();
                            }
                            setTimeout(resolve, lastReconnect);
                            lastReconnect = Math.min(10000, lastReconnect * 2);
                        }
                    })();
                });
                    // eslint-disable-next-line no-await-in-loop
                await promise;

                canConnect = session.isLoggedIn && (
                    session.permissions.has(Permission.ChatsModifyAll)
                            || session.permissions.has(Permission.TicketsReadSelf)
                            || session.permissions.has(Permission.TicketsReadAll));

                // eslint-disable-next-line no-constant-condition
            } while (canConnect);
        })();
    }, [session.userId]);

    return (
        <PortalSessionContext.Provider value={api}>
            {children}
        </PortalSessionContext.Provider>
    );
}

export default function useEvents(handler: (api: ScopedClientApi) => void, dependencies?: unknown[]): void {
    const api = useContext(PortalSessionContext) ?? {};
    api.registerEvents(handler, dependencies);
}
